import React from 'react';
import useScrollReveal from '../useScrollReveal';
import { sectionStyles, getAnimation, hexToRgba } from '../utils';
import SectionHeader from './SectionHeader';
import SectionDivider from './SectionDivider';

function dragScrollRef(el) {
    if (!el || el._dragScroll) return;
    el._dragScroll = true;
    let isDown = false, startX = 0, sl = 0, dragged = false;
    el.style.cursor = 'grab';
    el.onmousedown = (e) => { if (e.button !== 0) return; isDown = true; dragged = false; startX = e.pageX; sl = el.scrollLeft; el.style.cursor = 'grabbing'; el.style.userSelect = 'none'; };
    el.onmousemove = (e) => { if (!isDown) return; if (Math.abs(e.pageX - startX) > 5) dragged = true; if (dragged) { e.preventDefault(); el.scrollLeft = sl - (e.pageX - startX); } };
    el.onmouseup = () => { isDown = false; el.style.cursor = 'grab'; el.style.userSelect = ''; };
    el.onmouseleave = () => { isDown = false; el.style.cursor = 'grab'; el.style.userSelect = ''; dragged = false; };
    el.addEventListener('click', (e) => { if (dragged) { e.stopPropagation(); e.preventDefault(); dragged = false; } }, true);
}

export default function PlaybookCardsSection({ block = {}, primaryColor, secondaryColor, enableAnimations }) {
    const { config = {}, content = {} } = block;
    const s = sectionStyles(config, primaryColor, secondaryColor);
    const [ref, visible] = useScrollReveal(0.15, !enableAnimations);
    const items = content.items || [];
    const anim = getAnimation(config, visible);
    const variant = config.variant || 'scroll';

    if (items.length === 0) return null;

    const renderCard = (item, i) => {
        const num = item.number || String(i + 1).padStart(2, '0');
        return (
            <div
                key={i}
                className={`relative flex flex-col gap-3 rounded-2xl p-6 transition-all duration-300 ${
                    variant === 'stack' ? 'hover:-translate-y-2 hover:scale-[1.02]' : ''
                } ${variant === 'scroll' ? 'min-w-[300px] max-w-[300px]' : ''}`}
                style={{
                    background: hexToRgba(s.accent, 0.07),
                    border: `1px solid ${hexToRgba(s.accent, 0.15)}`,
                    ...(variant === 'stack' ? { marginTop: i > 0 ? '-12px' : 0, zIndex: items.length - i } : {}),
                }}
            >
                <span className="text-4xl font-bold leading-none" style={{ color: hexToRgba(s.accent, 0.3) }}>
                    {num}
                </span>
                <h3 className="text-lg font-semibold" style={{ color: s.text }}>
                    {item.title}
                </h3>
                <p className="text-sm line-clamp-2" style={{ color: s.textSub }}>
                    {item.description}
                </p>
                <div className="mt-auto flex items-center gap-2 pt-3">
                    {item.author_avatar ? (
                        <img src={item.author_avatar} alt="" className="w-6 h-6 rounded-full object-cover" />
                    ) : item.author ? (
                        <div className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white" style={{ background: s.accent }}>
                            {item.author.charAt(0).toUpperCase()}
                        </div>
                    ) : null}
                    {item.author && <span className="text-xs" style={{ color: s.textSub }}>{item.author}</span>}
                    {item.badge && (
                        <span
                            className="ml-auto text-xs font-medium px-2 py-0.5 rounded-full"
                            style={{ background: hexToRgba(s.secondary, 0.15), color: s.secondary }}
                        >
                            {item.badge}
                        </span>
                    )}
                </div>
            </div>
        );
    };

    return (
        <section ref={ref} style={{ background: s.dark }} className="py-16 md:py-24 overflow-hidden">
            <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
            <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                <SectionHeader
                    tag={content.tag}
                    title={content.title}
                    subtitle={content.subtitle}
                    s={s}
                    primaryColor={primaryColor}
                />

                {variant === 'scroll' && (
                    <div className="relative mt-10">
                        <div
                            ref={dragScrollRef}
                            className="flex gap-5 overflow-x-auto pb-4 scrollbar-hide"
                            style={{
                                WebkitMaskImage: 'linear-gradient(to right, transparent, black 5%, black 95%, transparent)',
                                maskImage: 'linear-gradient(to right, transparent, black 5%, black 95%, transparent)',
                            }}
                        >
                            {items.map(renderCard)}
                        </div>
                    </div>
                )}

                {variant === 'grid' && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-10">
                        {items.map(renderCard)}
                    </div>
                )}

                {variant === 'stack' && (
                    <div className="flex flex-col items-center mt-10 max-w-md mx-auto">
                        {items.map(renderCard)}
                    </div>
                )}
            </div>
            <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
        </section>
    );
}
